import { useState } from 'react';
import { useCreateProduct, useDeleteProduct, useProducts, useUpdateProduct } from '@/lib/api/catalogs';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { QueryState } from '@/components/QueryState';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { ProductForm } from '@/features/catalogs/ProductForm';
import type { CreateProductInput, Product, ProductStatus } from '@/lib/api/types';

const PAGE_SIZE = 50;

interface ProductsPanelProps {
  catalogId: string;
}

function EditProductForm({
  catalogId,
  product,
  onClose,
}: {
  catalogId: string;
  product: Product;
  onClose: () => void;
}) {
  const updateProduct = useUpdateProduct(catalogId, product.id);
  return (
    <ProductForm
      initial={product}
      onSubmit={(input) => updateProduct.mutate(input, { onSuccess: onClose })}
      onCancel={onClose}
      isPending={updateProduct.isPending}
      error={updateProduct.error as Error | null}
    />
  );
}

/** Paginated, filterable products table for a catalog, with inline create/edit (AC-6, AC-8). */
export function ProductsPanel({ catalogId }: ProductsPanelProps) {
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<ProductStatus | ''>('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [formMode, setFormMode] = useState<'closed' | 'new' | string>('closed');

  const products = useProducts(catalogId, {
    limit: PAGE_SIZE,
    offset,
    q: q || undefined,
    status: status || undefined,
    brand: brand || undefined,
    category: category || undefined,
  });
  const createProduct = useCreateProduct(catalogId);
  const deleteProduct = useDeleteProduct(catalogId);

  const items = products.data?.items ?? [];
  const total = products.data?.total ?? 0;
  const editingProduct = items.find((p) => p.id === formMode);

  function updateFilter<T>(setter: (value: T) => void, value: T) {
    setter(value);
    setOffset(0);
  }

  function handleCreate(input: CreateProductInput) {
    createProduct.mutate(input, { onSuccess: () => setFormMode('closed') });
  }

  function handleDelete(productId: string) {
    if (!window.confirm('Delete this product?')) return;
    deleteProduct.mutate(productId);
  }

  return (
    <Card
      title="Products"
      description="Browse, create, and edit this catalog's products."
      actions={
        formMode === 'closed' ? (
          <Button
            type="button"
            size="sm"
            onClick={() => setFormMode('new')}
            data-testid="new-product-button"
          >
            New product
          </Button>
        ) : undefined
      }
    >
      {formMode === 'new' && (
        <div className="mb-6 rounded-md border border-gray-200 p-4" data-testid="new-product-form">
          <ProductForm
            onSubmit={handleCreate}
            onCancel={() => setFormMode('closed')}
            isPending={createProduct.isPending}
            error={createProduct.error as Error | null}
          />
        </div>
      )}

      {editingProduct && (
        <div className="mb-6 rounded-md border border-gray-200 p-4" data-testid="edit-product-form">
          <EditProductForm
            catalogId={catalogId}
            product={editingProduct}
            onClose={() => setFormMode('closed')}
          />
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Input
          label="Search"
          value={q}
          onChange={(e) => updateFilter(setQ, e.target.value)}
          placeholder="SKU or name"
          data-testid="product-filter-q"
        />
        <Select
          label="Status"
          value={status}
          onChange={(e) => updateFilter(setStatus, e.target.value as ProductStatus | '')}
          data-testid="product-filter-status"
        >
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="discontinued">Discontinued</option>
        </Select>
        <Input
          label="Brand"
          value={brand}
          onChange={(e) => updateFilter(setBrand, e.target.value)}
          data-testid="product-filter-brand"
        />
        <Input
          label="Category"
          value={category}
          onChange={(e) => updateFilter(setCategory, e.target.value)}
          data-testid="product-filter-category"
        />
      </div>

      <QueryState
        isLoading={products.isLoading}
        error={products.error}
        isEmpty={items.length === 0}
        emptyMessage="No products match these filters."
        loadingLabel="Loading products"
        skeleton={<SkeletonRows rows={4} />}
      >
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase text-gray-500">
              <th className="py-2">SKU</th>
              <th className="py-2">Name</th>
              <th className="py-2">Brand</th>
              <th className="py-2">Category</th>
              <th className="py-2">Status</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((product) => (
              <tr key={product.id} data-testid="product-row">
                <td className="py-2">{product.sku}</td>
                <td className="py-2">{product.name}</td>
                <td className="py-2">{product.brand}</td>
                <td className="py-2">{product.category}</td>
                <td className="py-2">{product.status}</td>
                <td className="py-2 text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormMode(product.id)}
                    data-testid="edit-product-button"
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(product.id)}
                    data-testid="delete-product-button"
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            data-testid="prev-page-button"
          >
            Previous
          </Button>
          <span>
            {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            data-testid="next-page-button"
          >
            Next
          </Button>
        </div>
      )}
    </Card>
  );
}
